import { DestroyRef, Injectable } from '@angular/core';

import { BehaviorSubject, merge, Observable, timer } from 'rxjs';
import { concatMap, map, switchMap, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  MicrophoneSettings,
  SetLicenseSettings,
} from '@speaksee/speaksee-angular-core';
import {
  SettingsApiService,
  UpdateMicrophoneSettingsRequest,
} from './settings-api.service';

export enum Themes { // eslint-disable-line
  color = 'color-theme',
  glow = 'glow-theme',
  clean = 'clean-theme',
  teletekst = 'teletekst-theme',
}

export interface IRemoteSettings {
  license: SetLicenseSettings;
  microphones: MicrophoneSettings[];
}

export interface ILocalSettings {
  fontSize: number;
  fontWeight: number;
  selectedTheme: Themes;
  languageOut: string;
  showMetadata: boolean;
  segmentationSilenceTimeout: number;
  splitOnFinal: boolean;
  onlyShowChangesOnFinal: boolean;
  viewportWidth: number;
  closedBanners: string[];
  showConfidenceScore: boolean;
  darkMode: boolean | null; // null = follow system preference, true/false = user explicitly set
  ttsReadAlong: boolean; // developer-menu override that force-enables TTS read-along regardless of the device rollout list
}

export interface ISettings {
  kitSettings: IRemoteSettings;
  localSettings: ILocalSettings;
}

const defaultSettings: ISettings = {
  localSettings: {
    fontSize: 0,
    fontWeight: 400,
    selectedTheme: Themes.color,
    languageOut: 'en-US',
    showMetadata: false,
    segmentationSilenceTimeout: 2500,
    splitOnFinal: true,
    onlyShowChangesOnFinal: false,
    viewportWidth: 0,
    closedBanners:[],
    showConfidenceScore: false,
    darkMode: null,
    ttsReadAlong: false
  },
  kitSettings: {
    license: {
      custom_name: '',
      debug: false,
    } as any,
    microphones: [],
  },
};

@Injectable({
  providedIn: 'root',
})
export class SettingsService {

  public localLanguageOut$: Observable<string>;
  public localLanguageIn$: Observable<string>;
  public showMessageMetadata$: Observable<boolean>;
  public localSettings$: Observable<ILocalSettings>;
  public kit$: Observable<IRemoteSettings>;
  // accessiblity
  public fontSize$: Observable<number>;
  public fontWeight$: Observable<number>;
  public theme$: Observable<Themes>;
  public viewportWidth$: Observable<number>;
  public darkMode$: Observable<boolean>;

  private settings: ISettings;
  private showMessageMetadataSubject: BehaviorSubject<boolean>;
  private localLanguageOutSubject: BehaviorSubject<string>;
  private localLanguageInSubject: BehaviorSubject<string | null>;
  private kitSubject: BehaviorSubject<IRemoteSettings>;
  private localSettingsSubject: BehaviorSubject<ILocalSettings>;
  // accessiblity
  private fontSizeSubject: BehaviorSubject<number>;
  private fontWeightSubject: BehaviorSubject<number>;
  private themeSubject: BehaviorSubject<Themes>;
  private viewportWidthSubject: BehaviorSubject<number>;
  private darkModeSubject: BehaviorSubject<boolean>;

  private isPolling = undefined;

  constructor(
    private speakseeSettingsService: SettingsApiService,
    private destroyRef: DestroyRef
  ) {
    this.settings = Object.assign(defaultSettings);

    this.settings.localSettings = Object.assign(
      this.settings.localSettings,
      JSON.parse(localStorage.getItem('localSettings'))
    );

    // Migration: Sync languageOut with currentLanguage storage
    // Todo remove in future
    const currentLanguageStr = localStorage.getItem('currentLanguage');
    if (currentLanguageStr) {
      try {
        const currentLanguage = JSON.parse(currentLanguageStr);
        if (currentLanguage.translationLanguage &&
            this.settings.localSettings.languageOut !== currentLanguage.translationLanguage) {
          this.settings.localSettings.languageOut = currentLanguage.translationLanguage;
        }
      } catch (e) {
        // If it's the old string format, use it as both languages
        if (this.settings.localSettings.languageOut !== currentLanguageStr) {
          this.settings.localSettings.languageOut = currentLanguageStr;
        }
      }
    }
    this.showMessageMetadataSubject = new BehaviorSubject(
      this.settings.localSettings.showMetadata
    );
    this.showMessageMetadata$ = this.showMessageMetadataSubject.asObservable();

    this.localLanguageOutSubject = new BehaviorSubject(
      this.settings.localSettings.languageOut
    );
    this.localLanguageOut$ = this.localLanguageOutSubject.asObservable();

    this.localLanguageInSubject = new BehaviorSubject<string | null>(null);
    this.localLanguageIn$ = this.localLanguageInSubject.asObservable();

    this.localSettingsSubject = new BehaviorSubject(
      this.settings.localSettings
    );
    this.localSettingsSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => this.settings.localSettings = x);
    this.localSettings$ = this.localSettingsSubject.asObservable();

    this.kitSubject = new BehaviorSubject(this.settings.kitSettings);
    this.kit$ = this.kitSubject.asObservable();

    // accessiblity
    this.fontSizeSubject = new BehaviorSubject(
      this.settings.localSettings.fontSize
    );
    this.fontSize$ = this.fontSizeSubject.asObservable();
    this.fontSizeSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => this.settings.localSettings.fontSize = x);

    this.fontWeightSubject = new BehaviorSubject(
      this.settings.localSettings.fontWeight
    );
    this.fontWeight$ = this.fontWeightSubject.asObservable();
    this.fontWeightSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => this.settings.localSettings.fontWeight = x);

    this.themeSubject = new BehaviorSubject(
      this.settings.localSettings.selectedTheme
    );
    this.theme$ = this.themeSubject.asObservable();
    this.theme$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => this.settings.localSettings.selectedTheme = x);

    this.viewportWidthSubject = new BehaviorSubject(
      this.settings.localSettings.viewportWidth
    );
    this.viewportWidth$ = this.viewportWidthSubject.asObservable();
    this.viewportWidthSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(x => this.settings.localSettings.viewportWidth = x);

    const initialDarkMode = this.resolveInitialDarkMode();
    this.darkModeSubject = new BehaviorSubject(initialDarkMode);
    this.darkMode$ = this.darkModeSubject.asObservable();
    this.darkModeSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isDarkMode: boolean) => {
        if (sessionStorage.getItem('qrDarkMode') === null) {
          this.settings.localSettings.darkMode = isDarkMode;
        }
        this.applyThemeToDOM(isDarkMode);
      });

    // Apply initial theme
    this.applyThemeToDOM(initialDarkMode);

    merge(
      this.theme$,
      this.fontSize$,
      this.fontWeight$,
      this.localLanguageOut$,
      this.showMessageMetadata$,
      this.viewportWidth$,
      this.darkMode$,
      this.localSettingsSubject
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveLocalSettings());
  }

  get localSettings() {
    return this.localSettingsSubject.value;
  }

  private isQrAuthMode() {
    // Both QR and shared-session guests use the anonymous BE event variants.
    // Legacy QR mode lives in sessionStorage; shared-session mode moved to localStorage.
    return (
      sessionStorage.getItem('auth_mode') === 'qr' ||
      localStorage.getItem('auth_mode') === 'shared-session'
    );
  }

  updateLocalSettings(settings: ILocalSettings) {
    this.localSettingsSubject.next(settings);
  }

  changeFontSize(size: number): void {
    this.fontSizeSubject.next(size);
  }

  changeFontWeight(weight: number): void {
    this.fontWeightSubject.next(weight);
  }

  toggleDisplayMetadata(): void {
    this.settings.localSettings.showMetadata =
      !this.settings.localSettings.showMetadata;
    this.showMessageMetadataSubject.next(
      this.settings.localSettings.showMetadata
    );
  }

  changeTheme(theme: Themes) {
    this.themeSubject.next(theme);
  }

  getLocalLanguageOut(): string {
    return this.settings.localSettings.languageOut;
  }

  changeLocalLanguageOut(language: string) {
    this.settings.localSettings.languageOut = language;
    this.localLanguageOutSubject.next(language);

    const currentLanguageStr = localStorage.getItem('currentLanguage');
    if (currentLanguageStr) {
      try {
        const currentLanguage = JSON.parse(currentLanguageStr);
        currentLanguage.translationLanguage = language;
        localStorage.setItem('currentLanguage', JSON.stringify(currentLanguage));
      } catch (e) {
        // If parse fails, create new object with both languages set to this value
        const newLanguage = {
          transcriptionLanguage: language,
          translationLanguage: language
        };
        localStorage.setItem('currentLanguage', JSON.stringify(newLanguage));
      }
    } else {
      // No currentLanguage exists, create it
      const newLanguage = {
        transcriptionLanguage: language,
        translationLanguage: language
      };
      localStorage.setItem('currentLanguage', JSON.stringify(newLanguage));
    }
  }

  changeViewportWidth(width: number) {
    this.viewportWidthSubject.next(width);
  }

  getAvailableThemes(): string[] {
    return Object.keys(Themes);
  }

  getRemoteSetSettings(
    id?: number,
    forceReload?: boolean
  ): Observable<IRemoteSettings> {
    if (forceReload || !Object.keys(this.settings.kitSettings).length) {
      // get remote settings
      return this.speakseeSettingsService.getHubSetSettings(id).pipe(
        map((settings) => {
          this.kitSubject.next({
            license: settings.license_settings,
            microphones: settings?.microphone_settings?.map((mic, index) => ({
              ...mic,
              index: index + 1,
            })),
          } as IRemoteSettings);
          return this.kitSubject.value;
        }),
        switchMap(() => this.kitSubject)
      );
    }

    return this.kit$;
  }

  getMicSettings(id: number): MicrophoneSettings {
    if (!Object.keys(this.settings.kitSettings).length) {
      throw new Error(`no settings found try reloading the settings`);
    }
    // TODO: use mic for this
    const mic = this.kitSubject.value.microphones.find((m) => m.mic_id === id);
    if (!mic) {
      throw new Error(
        `no mic in settings found with id ${id} try reloading the settings`
      );
    }
    return mic;
  }
  /**
   * Update local microphone settings without API call
   * Used for QR users who send updates via WebSocket
   */
  updateLocalMicLanguageOut(languageCode: string, micId?: number): void {
    const currentSettings = this.kitSubject.value;
    const microphones = currentSettings?.microphones ?? [];
    if (!microphones.length) return;

    const micsToUpdate = micId !== undefined
      ? microphones.filter((m) => m.mic_id === micId)
      : microphones;

    micsToUpdate.forEach((mic) => mic.language_out = languageCode);
    this.kitSubject.next(currentSettings);
  }

  /**
   * Update local microphone language_in (recognition language) without API call
   * Used for QR users when receiving recognition language updates via WebSocket
   */
  updateLocalMicLanguageIn(languageCode: string, micId?: number): void {
    this.localLanguageInSubject.next(languageCode);

    const currentSettings = this.kitSubject.value;
    const microphones = currentSettings?.microphones ?? [];
    if (!microphones.length) return;

    const micsToUpdate = micId !== undefined
      ? microphones.filter((m) => m.mic_id === micId)
      : microphones;

    micsToUpdate.forEach((mic) => mic.language_in = languageCode);
    this.kitSubject.next(currentSettings);
  }

  // TODO: Use function for color selector implementation
  updateMicSettings(
    update: Omit<UpdateMicrophoneSettingsRequest, 'event'>
  ): Observable<Record<string, unknown>> {
    const request = {
      event: 'user.settings.language.update',
      microphones: update.microphones,
    } as UpdateMicrophoneSettingsRequest;

    if (this.isQrAuthMode()) {
      request.event = 'anonymous.settings.langauge_out.update';
    }

    const callback = this.speakseeSettingsService.updateMicSettings(request);

    return callback.pipe(
      map(() => {
        const currentSettings = this.kitSubject.value;
        for (const micUpdate of update.microphones) {
          const mic = currentSettings.microphones.find(
            (x) => x.mic_id === micUpdate.mic_id
          );
          mic.color_hex = micUpdate.color_hex ?? mic.color_hex;
          mic.language_in = micUpdate.language_in ?? mic.language_in;
          mic.language_out = micUpdate.language_out ?? mic.language_out;
          mic.name = micUpdate.name ?? mic.name;
        }
        this.kitSubject.next(currentSettings);
        return {};
      })
    );
  }

  public startMicSettingsPoller(stopPolling: Observable<any>) {
    if (this.isPolling) {
      console.log('polling is already started');
      return;
    }
    this.isPolling = timer(0, 10 * 1000)
      .pipe(
        takeUntil(stopPolling),
        concatMap(() => {
          const request = {
            event: 'user.settings.language.update',
          } as UpdateMicrophoneSettingsRequest;

          if (this.isQrAuthMode()) {
            request.event = 'anonymous.settings.langauge_out.update';
          }
          request.microphones = this.kitSubject.value.microphones.map(
            (mic) =>
              ({
                mic_id: mic.mic_id,
                language_out: mic.language_out,
              } as MicrophoneSettings)
          );
          return this.speakseeSettingsService.updateMicSettings(request);
        })
      )
      .subscribe({
        complete: () => {
          this.isPolling = undefined;
        },
      });
  }

  private saveLocalSettings() {
    localStorage.setItem(
      'localSettings',
      JSON.stringify(this.settings.localSettings)
    );
  }

  toggleDarkMode(): void {
    this.darkModeSubject.next(!this.darkModeSubject.value);
  }

  setDarkMode(isDarkMode: boolean): void {
    this.darkModeSubject.next(isDarkMode);
  }

  setQrDarkMode(isDarkMode: boolean): void {
    sessionStorage.setItem('qrDarkMode', String(isDarkMode));
    this.setDarkMode(isDarkMode);
  }

  clearQrDarkMode(): void {
    sessionStorage.removeItem('qrDarkMode');
    const resolved = this.resolveInitialDarkMode();
    this.setDarkMode(resolved);
  }

  private applyThemeToDOM(isDarkMode: boolean): void {
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }

  private resolveInitialDarkMode(): boolean {
    // Priority 1: QR dark mode param (stored in session)
    const qrDarkMode = sessionStorage.getItem('qrDarkMode');
    if (qrDarkMode !== null) {
      return qrDarkMode === 'true';
    }

    // Priority 2: User explicit preference (null means follow system)
    if (this.settings.localSettings.darkMode !== null &&
        this.settings.localSettings.darkMode !== undefined) {
      return this.settings.localSettings.darkMode;
    }

    // Priority 3: System preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
}
